import type { CignaPrincipal } from '@uit/shared/schemas/rcf';
import { useState } from 'react';
import s from './rcf-settings.module.scss';

// Cigna principals (Sam/Chris) — name + email; one entry is both the manager's-
// principal RCF email recipient and the expense-center value (T-113). A manager
// references one of these. Props-driven so it shares principals with the
// managers section (RcfReferenceSections) — onChange reloads BOTH so the
// managers dropdown + rows never go stale after a principal add/remove.
interface Props {
  principals: CignaPrincipal[] | undefined;
  onChange: () => void;
}

export function CignaPrincipalsSection({ principals, onChange }: Props) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function add(): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      const r = await fetch('/api/settings/cigna-principals', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim() }),
      });
      if (!r.ok) {
        setActionError(r.status === 403 ? 'You do not have permission.' : `Could not add (${r.status}).`);
        return;
      }
      setName('');
      setEmail('');
    } finally {
      setBusy(false);
      onChange();
    }
  }

  async function remove(id: string): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      const r = await fetch(`/api/settings/cigna-principals/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok && r.status !== 204) setActionError(`Could not remove (${r.status}).`);
    } finally {
      setBusy(false);
      onChange();
    }
  }

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Cigna principals</h2>
      <p className={s.sectionSub}>
        The principals a Cigna manager rolls up to (Sam, Chris). One entry serves two RCF roles: the
        email is the manager&rsquo;s-principal recipient on the RCF email, and the name is the
        expense-center value on the RCF file.
      </p>

      {actionError && (
        <div className={s.error} role="alert">
          {actionError}
        </div>
      )}
      {!principals && <div className={s.muted}>Loading…</div>}

      {principals && principals.length > 0 && (
        <table className={s.table}>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col" className={s.right}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {principals.map((p) => (
              <tr key={p.id}>
                <td>
                  <span className={s.pill}>{p.name}</span>
                </td>
                <td>{p.email}</td>
                <td className={s.right}>
                  <button
                    type="button"
                    className={s.removeBtn}
                    disabled={busy}
                    onClick={() => void remove(p.id)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {principals && principals.length === 0 && (
        <div className={s.muted}>No principals yet — add one below.</div>
      )}

      <div className={s.addRow}>
        <div className={s.field}>
          <label className={s.label} htmlFor="cp-name">
            Principal name
          </label>
          <input
            id="cp-name"
            className={s.input}
            value={name}
            placeholder="e.g. Sam"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className={s.field}>
          <label className={s.label} htmlFor="cp-email">
            Principal email
          </label>
          <input
            id="cp-email"
            type="email"
            className={s.input}
            value={email}
            placeholder="sam@cigna.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <button
          type="button"
          className={s.addBtn}
          disabled={busy || name.trim() === '' || email.trim() === ''}
          onClick={() => void add()}
        >
          Add
        </button>
      </div>
    </section>
  );
}
